import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Pencil, RotateCcw, Eye, Code, Save, Tag } from 'lucide-react';
import api from '@/lib/api';

const CATEGORY_COLORS = {
  leave: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  policy: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  performance: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  onboarding: 'bg-purple-100 text-purple-700 hover:bg-purple-100',
};

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [tab, setTab] = useState('edit');

  const load = async () => {
    try {
      const res = await api.get('/api/email-templates');
      setTemplates(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setEditTemplate(t);
    setEditSubject(t.subject);
    setEditBody(t.body);
    setPreviewHtml('');
    setTab('edit');
    setEditOpen(true);
  };

  const handlePreview = async () => {
    try {
      const res = await api.post('/api/email-templates/preview', { body: editBody });
      setPreviewHtml(res.data.preview_html);
      setTab('preview');
    } catch (e) { toast.error('Preview failed'); }
  };

  const handleSave = async () => {
    try {
      await api.put(`/api/email-templates/${editTemplate.id}`, { subject: editSubject, body: editBody });
      toast.success('Template saved');
      setEditOpen(false);
      load();
    } catch (e) { toast.error('Save failed'); }
  };

  const handleReset = async (id) => {
    if (!window.confirm('Reset to default template?')) return;
    try {
      await api.post(`/api/email-templates/reset/${id}`);
      toast.success('Reset to default');
      load();
    } catch (e) { toast.error('Reset failed'); }
  };

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      <div>
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// SETTINGS</Badge>
        <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Email Templates</h1>
        <p className="text-sm text-slate-500">Customize notification email content with placeholders</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Template</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Placeholders</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                  <TableCell className="font-medium text-sm">{t.name}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${CATEGORY_COLORS[t.category] || ''}`}>{t.category}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-xs truncate">{t.subject}</TableCell>
                  <TableCell className="flex flex-wrap gap-1">
                    {(t.placeholders || []).map(p => (
                      <Badge key={p} variant="outline" className="text-[10px] font-mono">{`{${p}}`}</Badge>
                    ))}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(t)} title="Edit" data-testid={`edit-template-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={() => handleReset(t.id)} title="Reset to Default" data-testid={`reset-template-${t.id}`}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {templates.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400 text-sm">{loading ? 'Loading...' : 'No templates'}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" />Edit: {editTemplate?.name}</DialogTitle></DialogHeader>
          {editTemplate && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                {(editTemplate.placeholders || []).map(p => (
                  <Badge key={p} variant="outline" className="text-xs font-mono cursor-pointer hover:bg-slate-100"
                    onClick={() => { setEditBody(prev => prev + `{${p}}`); }}
                    title="Click to insert">
                    {`{${p}}`}
                  </Badge>
                ))}
              </div>

              <div><Label>Subject Line</Label>
                <Input value={editSubject} onChange={e => setEditSubject(e.target.value)} data-testid="template-subject" />
              </div>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="edit"><Code className="w-3.5 h-3.5 mr-1" />HTML Editor</TabsTrigger>
                  <TabsTrigger value="preview"><Eye className="w-3.5 h-3.5 mr-1" />Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="edit">
                  <Textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={14} className="font-mono text-xs" data-testid="template-body" />
                </TabsContent>
                <TabsContent value="preview">
                  {previewHtml ? (
                    <div className="border rounded-lg p-4 bg-white" data-testid="template-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  ) : (
                    <div className="text-center py-8 text-slate-400 text-sm">Click "Preview" below to render with sample data</div>
                  )}
                </TabsContent>
              </Tabs>

              <div className="flex gap-2">
                <Button onClick={handlePreview} variant="outline" className="flex-1" data-testid="template-preview-btn">
                  <Eye className="w-3.5 h-3.5 mr-1" />Preview
                </Button>
                <Button onClick={handleSave} className="flex-1 bg-[#0D2850] hover:bg-[#0B2246]" data-testid="template-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1" />Save Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
